import { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import { ICameraWindowSize } from "../interfaces/ICameraWindowSize";
import { LoadingFactory } from "src/factory/loading.factory";

export namespace CameraFacade {

  const ANIMATION_TIME: number = 250;

  const ANIMATION = {
    IN: `${ANIMATION_TIME}ms ease-out forwards cameraLibIn`,
    OUT: `${ANIMATION_TIME}ms ease-in forwards cameraLibOut`,
  }

  const FALLBACK_CONSTRAINTS = {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    facingMode: "environment",
  };

  export const useCamera = (cameraAvailable: boolean | undefined, onSelect: (picture: string) => void) => {
    const [currentPicture, setCurrentPicture] = useState<string | undefined>();
    const [videoConstraints, setVideoConstraints] = useState<{
      width: { ideal: number },
      height: { ideal: number },
      facingMode: string
    } | undefined>(undefined);
    const [currentAnimation, setCurrentAnimation] = useState<string>(ANIMATION.IN);
    const [windowSize, setWindowSize] = useState<ICameraWindowSize>({ width: 0, height: 0 });
    const isLoading = LoadingFactory.useLoader(true);
    // El setter de useState es estable entre renders; lo extraemos para poder
    // listarlo en las dependencias del efecto sin re-disparar getUserMedia
    // (el objeto `isLoading` se recrea en cada render).
    const setLoading = isLoading.set;
    const webcamRef = useRef<Webcam>(null);

    /**
     *
     */
    const takePicture = useCallback(
      () => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (imageSrc) {
          setCurrentPicture(imageSrc);
        }
      }, [webcamRef]
    );

    /**
     *
     */
    const removeCurrentPicture = (): void => {
      setCurrentPicture(undefined);
    }

    /**
     *
     */
    const selectCurrentPicture = (onCancel: () => void): void => {
      if (currentPicture) {
        onSelect(currentPicture);
        cancelPicture(onCancel);
      }
    }

    /**
     *
     */
    const cancelPicture = (onCancel: () => void): void => {
      setCurrentAnimation(ANIMATION.OUT);
      setTimeout(() => {
        onCancel();
      }, ANIMATION_TIME);
    }

    /**
     * Detecta la mejor resolución soportada por la cámara del dispositivo.
     * Forzar una resolución fija (p.ej. 1920x1920) provoca pantalla en negro en
     * equipos que no la soportan; aquí consultamos las capacidades reales del
     * track y ajustamos el ancho/alto ideal según la orientación de la pantalla.
     */
    useEffect(() => {
      if (!cameraAvailable) return;
      let isMounted = true;

      async function findBestResolution() {
        let stream: MediaStream | null = null;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' }
          });

          const track = stream.getVideoTracks()[0];
          if (!track) {
            throw new Error("No se encontró un track de video en el stream.");
          }

          const capabilities = track.getCapabilities();
          stream.getTracks().forEach(t => t.stop());
          stream = null;

          const maxWidth = capabilities.width?.max;
          const maxHeight = capabilities.height?.max;

          if (!isMounted) return;

          if (maxWidth && maxHeight) {
            const isPortrait = window.innerHeight > window.innerWidth;
            const idealWidth = isPortrait ? Math.min(maxWidth, maxHeight) : Math.max(maxWidth, maxHeight);
            const idealHeight = isPortrait ? Math.max(maxWidth, maxHeight) : Math.min(maxWidth, maxHeight);

            setVideoConstraints({
              width: { ideal: idealWidth },
              height: { ideal: idealHeight },
              facingMode: "environment"
            });
          } else {
            setVideoConstraints(FALLBACK_CONSTRAINTS);
          }
        } catch (e) {
          if (stream)
            stream.getTracks().forEach(t => t.stop());
          if (isMounted)
            setVideoConstraints(FALLBACK_CONSTRAINTS);
          console.error(e);
        } finally {
          if (isMounted)
            setLoading(false);
        }
      }

      findBestResolution();

      // El stream del <Webcam> se libera en el efecto de desmontaje dedicado;
      // aquí solo evitamos setear estado tras desmontar.
      return () => {
        isMounted = false;
      };
    }, [cameraAvailable, setLoading]);

    useEffect(() => {
      const handleResize = () => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }

      window.addEventListener("resize", handleResize);
      handleResize();

      return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
      return () => {
        // Debe leerse en el cleanup: el stream lo asigna <Webcam> tras montar,
        // por lo que capturar la ref al inicio del efecto daría null.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const stream = webcamRef.current?.stream;
        if (stream)
          stream.getTracks().forEach(track => track.stop());
      };
    }, []);

    return {
      states: {
        windowSize,
        webcamRef,
        currentPicture,
        currentAnimation,
        videoConstraints,
        isLoading,
      },
      actions: {
        takePicture,
        removeCurrentPicture,
        cancelPicture,
        selectCurrentPicture,
      }
    };
  }


  /**
   *
   * @returns
   */
  export const usePermissions = () => {
    const [cameraAvailable, setCameraAvailable] = useState<boolean | undefined>(undefined);

    const verifyPermissions = useCallback(
      async () => {
        try {
          const constraints: MediaStreamConstraints = {
            video: true
          };
          const stream = await navigator.mediaDevices.getUserMedia(constraints);

          // Liberamos de inmediato el stream de verificación. Mantenerlo abierto
          // bloqueaba el acceso a la cámara del componente Webcam en algunos
          // dispositivos, provocando pantalla en negro.
          stream.getTracks().forEach(track => track.stop());
          setCameraAvailable(true);
        } catch (e) {
          setCameraAvailable(false);
          console.error(e)
        }
      }, []
    );

    const useMountEffect = () => useEffect(
      () => {
        verifyPermissions();
      }, []
    );
    useMountEffect();

    return { verifyPermissions, cameraAvailable }
  }
}
